import { Box } from '@mui/material';

import {
  CAMPAIGN_CARD_IMAGE_ASPECT_RATIO_PC,
  CAMPAIGN_CARD_IMAGE_ASPECT_RATIO_SP,
} from '@common/constants';
import { useCampaignCardImageReveal } from '@common/hooks/useCampaignCardImageReveal';

const CAMPAIGN_IMAGE_ANIMATION =
  'campaignImageFadeIn 550ms cubic-bezier(0.22, 1, 0.36, 1) both';

const hiddenImageSx = {
  opacity: 0,
  transform: 'translateY(8px)',
};

type CampaignCardImageProps = {
  alt: string;
  revealKey: string;
  srcPc: string;
  srcSp: string;
};

const imageBorderSx = (theme: { colors: { secondary: string } }) => ({
  border: `1px solid ${theme.colors.secondary}`,
  borderRadius: '10px',
});

/**
 * Responsive campaign card image that fades in when scrolled into view
 */
export function CampaignCardImage({
  alt,
  revealKey,
  srcPc,
  srcSp,
}: CampaignCardImageProps) {
  const { containerRef, revealed } = useCampaignCardImageReveal(
    `${revealKey}:${srcPc}:${srcSp}`
  );

  return (
    <Box
      ref={containerRef}
      sx={{
        '@keyframes campaignImageFadeIn': {
          from: hiddenImageSx,
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        width: '100%',
      }}
    >
      <Box
        alt={alt}
        component="img"
        src={srcPc}
        sx={(theme) => ({
          ...imageBorderSx(theme),
          aspectRatio: CAMPAIGN_CARD_IMAGE_ASPECT_RATIO_PC,
          display: { md: 'block', xs: 'none' },
          height: '100%',
          objectFit: 'cover',
          width: '100%',
          ...(revealed
            ? { animation: CAMPAIGN_IMAGE_ANIMATION }
            : hiddenImageSx),
        })}
      />
      <Box
        alt={alt}
        component="img"
        src={srcSp}
        sx={(theme) => ({
          ...imageBorderSx(theme),
          aspectRatio: CAMPAIGN_CARD_IMAGE_ASPECT_RATIO_SP,
          display: { md: 'none', xs: 'block' },
          height: '100%',
          objectFit: 'cover',
          width: '100%',
          ...(revealed
            ? { animation: CAMPAIGN_IMAGE_ANIMATION }
            : hiddenImageSx),
        })}
      />
    </Box>
  );
}
